import React from 'react';
import { StatusBar } from 'react-native';
import Animated, { Extrapolate, interpolate, useAnimatedStyle } from 'react-native-reanimated';
import Text, { lineHeight } from 'design-system/Text';
import Spacer from 'design-system/Spacer';
import { rem } from 'design-system/values';
import { useHeaderHeight } from 'hooks';
import { useDefaultHeaderHeight, useStatusBarHeight } from 'hooks/useHeaderHeight';
import createStyleSheets from 'utils/createStyleSheets';
import useStyles from 'hooks/useStyles';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
export const strings = {
    Address: {
        title: 'What’s your current permanent\xa0address?',
        titleLines: 2,
        shortTitle: 'Permanent address',
        subtitle: 'We are required to ask this information for\xa0regulatory\xa0purposes',
        subtitleLines: 2,
    },
    Income: {
        title: 'What is your annual household\xa0income?',
        titleLines: 2,
        shortTitle: 'Annual Household Income',
        subtitle: 'This includes income from sources such as employment, investments, benefits or other',
        subtitleLines: 3,
    },
    Insurance: {
        title: 'What is your National Insurance\xa0number?',
        titleLines: 2,
        shortTitle: 'Insurance',
        subtitle: 'We need this to open your account. It’s a regulatory requirement',
        subtitleLines: 2,
    },
    NetWorth: {
        title: 'What are your Investible liquid\xa0assets?',
        titleLines: 2,
        shortTitle: 'Investible Liquid Assets',
        subtitle: 'These are all the assets that can be converted quickly and easily into cash',
        subtitleLines: 2,
    },
    Funding: {
        title: 'What is your funding source?',
        titleLines: 2,
        shortTitle: 'Funding Source',
        subtitle: 'This is where the money you are going to deposit in Emma comes\xa0from',
        subtitleLines: 2,
    },
    Employment: {
        title: 'What is your employment status?',
        titleLines: 2,
        shortTitle: 'Employment Status',
        subtitle: 'We are required to ask this information for regulatory purposes',
        subtitleLines: 2,
    },
    Affiliations: {
        title: 'Please confirm that none of these apply to you',
        titleLines: 2,
        shortTitle: '',
        subtitleLines: 0,
    },
    Affiliations2: {
        title: 'Which ones of these apply?',
        titleLines: 2,
        shortTitle: 'Affiliations',
        subtitleLines: 0,
    },
    W8: {
        title: 'W-8 disclosures and tax form',
        titleLines: 2,
        shortTitle: 'W-8 Disclosures',
        subtitle: 'Tick all the boxes to proceed',
        subtitleLines: 1,
    },
    Terms: {
        title: 'Terms and conditions',
        titleLines: 1,
        shortTitle: 'Terms and conditions',
        subtitle: 'To open your account, review and agree to the documents below',
        subtitleLines: 2,
    },
    SelectDocument: {
        titleLines: 1,
        title: 'Upload proof of identity',
        subtitleLines: 2,
        shortTitle: 'Upload Identity',
        subtitle: 'Make sure your personal details are visible and the document is in the frame.',
    },
    ChooseProofOfAddress: {
        titleLines: 1,
        title: 'Upload proof of address',
        shortTitle: 'Proof of Address',
        subtitleLines: 2,
        subtitle: 'Make sure the document is issued within 3 months and your name is visible.',
    },
    SetupPots: {
        titleLines: 1,
        title: 'Setup Pots for free',
        shortTitle: 'Setup Pots',
        subtitleLines: 3,
        subtitle: 'We are required to ask this information for\xa0regulatory\xa0purposes. It takes less than 5 minutes',
    },
    SetupWallet: {
        titleLines: 1,
        title: 'Setup Wallet for free',
        shortTitle: 'Setup Emma wallet',
        subtitleLines: 3,
        subtitle: 'We are required to ask this information for\xa0regulatory\xa0purposes. It takes less than 5 minutes',
    },
    SetupInvestmentAccount: {
        titleLines: 1,
        title: 'Set up your account',
        shortTitle: 'Setup account',
        subtitleLines: 3,
        subtitle: 'We are required to ask this information for\xa0regulatory\xa0purposes. It takes less than 5 minutes',
    },
    ConfirmPersonalDetails: {
        titleLines: 1,
        title: 'Review your details',
        shortTitle: 'Review your details',
        subtitleLines: 2,
        subtitle: 'We might ask you to verify these details with a document',
    },
    ConfirmAddress: {
        titleLines: 1,
        title: 'Review your address',
        shortTitle: 'Review your address',
        subtitleLines: 2,
        subtitle: 'We might ask you to verify these details with\xa0a\xa0document. Make sure they are correct',
    },
    TransferDetails: {
        titleLines: 2,
        title: 'Make a bank transfer to your\xa0Emma\xa0account',
        shortTitle: 'Bank Transfer Details',
        subtitleLines: 1,
        subtitle: 'You can do this from your bank account',
    },
    Interval: {
        titleLines: 1,
        title: 'Interval',
        subtitleLines: 2,
        shortTitle: 'Interval',
        subtitle: 'Specify the interval at which your recurring buy shall be executed',
    },
    Holidays: {
        titleLines: 1,
        subtitleLines: 2,
        title: 'Adjust for holidays',
        shortTitle: 'Adjust for holidays',
        subtitle: 'If your selected date falls under a holiday, choose when it shall be executed instead',
    },
    SelectDate: {
        titleLines: 1,
        subtitleLines: 2,
        title: 'Select a date',
        shortTitle: 'Select a date',
        subtitle: 'Specify day of the month when the order should\xa0be\xa0executed',
    },
    SelectWeekday: {
        titleLines: 1,
        subtitleLines: 2,
        title: 'Select a week day',
        shortTitle: 'Select a week day',
        subtitle: 'Specify day of the week when the order should\xa0be\xa0executed',
    },
};
/**
 * @param flex Should be used together with `scrollEnabled={false}` and `headerShouldUseFlex` on the ScrollView.
 */
const OnboardingTitle = ({ fixed, flex, scrollY, black, noShortTitle, mountedOnAnotherScreen, hideSpacingOnTop = false, ...props }) => {
    const stackHeaderHeight = useHeaderHeight();
    const statusBarHeight = useStatusBarHeight();
    const defaultHeaderHeight = useDefaultHeaderHeight();
    const styles = useStyles(styleSet);
    const headerHeight = mountedOnAnotherScreen ? defaultHeaderHeight : stackHeaderHeight;
    const containerOpacityStyle = useAnimatedStyle(() => ({
        opacity: interpolate(scrollY.value, [20, 60], [1, 0]),
    }));
    const containerPositionStyle = useAnimatedStyle(() => ({
        top: -scrollY.value,
    }));
    const titleStyle = useAnimatedStyle(() => ({
        top: interpolate(scrollY.value, [40, 80], [-10, 0], {
            extrapolateLeft: Extrapolate.CLAMP,
            extrapolateRight: Extrapolate.CLAMP,
        }),
        opacity: interpolate(scrollY.value, [40, 80], [0, 1]),
    }));
    const titleColorStyle = black ? { OffBlack: true } : { White: true };
    const subtitleColorStyle = black ? { Gray: true } : { White: true };
    return (<>
      {!noShortTitle && (<Animated.View style={[
                styles.title,
                titleStyle,
                {
                    height: headerHeight,
                    paddingTop: statusBarHeight,
                },
            ]}>
          <Text Text-18 White centered>
            {strings[props.for].shortTitle}
          </Text>
        </Animated.View>)}
      <Animated.View style={[
            styles.container,
            containerOpacityStyle,
            !fixed && {
                paddingTop: headerHeight,
            },
            !fixed && !flex && styles.absolute,
            !fixed && !flex && containerPositionStyle,
        ]}>
        {!fixed && <StatusBar barStyle="light-content" animated/>}
        {!hideSpacingOnTop && <Spacer h={TOP_SPACING}/>}
        <Text Text-24 {...titleColorStyle}>
          {strings[props.for].title}
        </Text>
        {!!strings[props.for].subtitle && (<>
            <Spacer h={SPACING}/>
            <Text TextThin-16 {...subtitleColorStyle}>
              {strings[props.for].subtitle}
            </Text>
          </>)}
      </Animated.View>
      {!!(fixed || flex) && <Spacer h={BOTTOM_SPACING}/>}
    </>);
};
const TOP_SPACING = 12;
const BOTTOM_SPACING = 22;
const SPACING = 4;
const styleSet = createStyleSheets((colors) => ({
    absolute: {
        position: 'absolute',
        left: 0,
        right: 0,
    },
    container: {
        paddingHorizontal: rem(16),
    },
    title: {
        left: 0,
        right: 0,
        zIndex: 4,
        position: 'absolute',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.background.brand,
    },
}));
export const useOnboardingTitleHeight = (screen, hideSpacingOnTop = false, mountedOnAnotherScreen = false) => {
    const stackHeaderHeight = useHeaderHeight();
    const defaultHeaderHeight = useDefaultHeaderHeight();
    const headerHeight = mountedOnAnotherScreen ? defaultHeaderHeight : stackHeaderHeight;
    const { titleLines, subtitleLines } = strings[screen];
    return (headerHeight +
        (hideSpacingOnTop ? 0 : rem(TOP_SPACING)) +
        rem(lineHeight.Text_24 * titleLines) +
        rem(SPACING) +
        rem(lineHeight.TextThin_16 * subtitleLines) +
        rem(BOTTOM_SPACING));
};
export const useOnboardingFinalHeight = (mountedOnAnotherScreen = true) => {
    const defaultHeaderHeight = useDefaultHeaderHeight();
    const { top } = useSafeAreaInsets();
    if (mountedOnAnotherScreen) {
        return defaultHeaderHeight - top;
    }
    return undefined;
};
export default OnboardingTitle;
